/**
 * LoginPage — Multi-method auth: Phone OTP · Email OTP · Password · Google
 * OTP-first approach matching the mobile app's login flow
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, Brain, ScrollText, Clock, Eye, EyeOff, Phone, Mail, Lock, CheckCircle, User, MapPin, Calendar } from 'lucide-react';
import { getBackendUrl } from '../config/api';
import { clientHeaders, guestHeaders } from '../utils/clientHeaders';
import { peekReportHandoff } from '../utils/reportHandoff';
import { getFbp } from '../lib/metaPixel';
import { useAuth } from '../contexts/AuthContext';
import { trackEvent } from '../utils/analytics';
import { peekAuthNotice, clearAuthNotice } from '../utils/authNotice';
import LegalFooter from './LegalFooter';

const GOOGLE_CLIENT_ID = '253524796752-2g2f1arvj8tci9lginon4a2dnc1qif5a.apps.googleusercontent.com';
const API = getBackendUrl();

const TABS = [
  { key: 'mobile', label: 'Mobile', icon: Phone },
  { key: 'email', label: 'Email OTP', icon: Mail },
  { key: 'password', label: 'Password', icon: Lock },
];

/* ── OTP Input Component ────────────────────────────────────────────────── */
function OtpInput({ value, onChange, disabled }) {
  const refs = useRef([]);

  function handleChange(idx, char) {
    const clean = (char || '').replace(/\D/g, '').slice(-1);
    const next = [...value];
    next[idx] = clean;
    onChange(next);
    if (clean && idx < 5) refs.current[idx + 1]?.focus();
  }

  function handleKeyDown(idx, e) {
    if (e.key === 'Backspace' && !value[idx] && idx > 0) {
      refs.current[idx - 1]?.focus();
    }
  }

  return (
    <div className="flex justify-between gap-2" data-testid="otp-input-group">
      {Array.from({ length: 6 }).map((_, i) => (
        <input
          key={i}
          ref={el => (refs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[i] || ''}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          disabled={disabled}
          className="w-11 h-13 rounded-xl bg-white/5 border border-amber-500/25 text-white text-center text-xl font-bold focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/30 transition disabled:opacity-50"
          data-testid={`otp-box-${i}`}
        />
      ))}
    </div>
  );
}

/* ── Main Login Page ────────────────────────────────────────────────────── */
const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleAuthCallback } = useAuth();

  const [isLoading, setIsLoading] = useState(() => {
    try {
      const hasLead = new URLSearchParams(window.location.search).has('lead_id');
      const hasToken = !!(localStorage.getItem('jyotish_session_token') || localStorage.getItem('jyotish_auth_user'));
      return hasLead || hasToken;
    } catch {
      return false;
    }
  });
  const [activeTab, setActiveTab] = useState('mobile');
  // A one-shot notice (e.g. "Your session expired…") left by the page that
  // sent us here; read in the initializer, removed once shown.
  const [error, setError] = useState(() => location.state?.error || peekAuthNotice());
  useEffect(() => { clearAuthNotice(); }, []);

  // Phone OTP state
  const [phone, setPhone] = useState('');
  const [phoneOtp, setPhoneOtp] = useState(['', '', '', '', '', '']);
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [sendingPhoneOtp, setSendingPhoneOtp] = useState(false);
  const [verifyingPhoneOtp, setVerifyingPhoneOtp] = useState(false);
  const [phoneCountdown, setPhoneCountdown] = useState(0);
  const [phoneError, setPhoneError] = useState('');
  const [phoneOtpError, setPhoneOtpError] = useState('');

  // Email OTP state
  const [email, setEmail] = useState('');
  const [emailOtp, setEmailOtp] = useState(['', '', '', '', '', '']);
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [sendingEmailOtp, setSendingEmailOtp] = useState(false);
  const [verifyingEmailOtp, setVerifyingEmailOtp] = useState(false);
  const [emailCountdown, setEmailCountdown] = useState(0);
  const [emailError, setEmailError] = useState('');
  const [emailOtpError, setEmailOtpError] = useState('');

  // Password state
  const [authMode, setAuthMode] = useState('login');
  const [pwdEmail, setPwdEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwdSubmitting, setPwdSubmitting] = useState(false);
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [pwdEmailError, setPwdEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [nameError, setNameError] = useState('');

  // Google
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const googleButtonRef = useRef(null);
  const oneTapInitialized = useRef(false);

  // Lead prefill state
  const [leadFetching, setLeadFetching] = useState(false);
  const [leadPrefill,  setLeadPrefill]  = useState(null);
  // Opaque landing-funnel lead id from the URL (no PII in the URL). Carried
  // through OTP verify so the backend can link lead↔user at account creation.
  const [leadId,       setLeadId]       = useState(null);

  /* ── Mount: hydrate from lead_id BEFORE the auth check can redirect ───────
     The landing funnel passes ONLY lead_id + intent + utm in the URL (no PII).
     Two things broke prefill in prod:
       1. The auth check (/api/auth/me) redirected returning users to
          /dashboard|/onboarding and unmounted LoginPage before the prefill
          effect (gated on isLoading) ever ran.
       2. Prefill was gated behind isLoading, so it only ran on the non-OK auth
          path.
     Fix: a single ordered mount effect that ALWAYS runs the lead hydration
     first (fetch /api/leads/prefill, prefill the phone field, write the
     guestBirthData + pending-sync bridges), THEN does the auth check. The
     storage writes are plain (not React state) so they survive an immediate
     redirect — onboarding pre-fills even for a returning user with a session. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const urlLeadId = (params.get('lead_id') || '').trim();

      // Persist entry intent for the dashboard's intent-aware actions.
      const entryIntent = params.get('intent') || params.get('trial_intent');
      if (entryIntent) {
        try { sessionStorage.setItem('jp_entry_intent', entryIntent.toLowerCase()); } catch {}
      }

      // 1. Lead hydration FIRST (only when a non-empty lead_id is present).
      if (urlLeadId) {
        setLeadId(urlLeadId);       // carried through OTP verify for lead↔user linkage
        setActiveTab('mobile');     // phone-based flow

        // Track the WhatsApp CTA tap — once per session per lead. The landing
        // insight WhatsApp deep-links here with ?lead_id=…&source=whatsapp; a
        // present lead_id IS the tap. `funnel_cta_clicked` was in the registry
        // but never fired (cta_tapped_count read 0). Also persist server-side
        // so landing-analytics reads leads.cta_tapped_at directly.
        try {
          const ctaKey = `jp_cta_tapped_${urlLeadId}`;
          if (!sessionStorage.getItem(ctaKey)) {
            sessionStorage.setItem(ctaKey, '1');
            const ctaSource = (params.get('source') || 'whatsapp');
            const ctaPath = window.location.pathname;
            trackEvent('funnel_cta_clicked', { lead_id: urlLeadId, source: ctaSource, path: ctaPath });
            fetch(`${API}/api/leads/cta-tapped`, {
              method: 'POST',
              keepalive: true,
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ lead_id: urlLeadId, source: ctaSource, path: ctaPath }),
            }).catch(() => {});
          }
        } catch { /* sessionStorage blocked (private mode) — skip dedup, don't block */ }
        setLeadFetching(true);
        try {
          const res = await fetch(`${API}/api/leads/prefill?lead_id=${encodeURIComponent(urlLeadId)}`);
          if (res.ok) {
            const data = await res.json();

            // Pre-fill the real (unmasked) phone — field stays editable.
            const phoneDigits = (data.phone || '').replace(/\D/g, '').slice(-10);
            if (!cancelled && phoneDigits.length === 10) setPhone(phoneDigits);

            // DOB from backend is DD-MM-YYYY → convert to YYYY-MM-DD.
            const dobRaw = data.dob || '';
            let isoDate = '';
            if (dobRaw) {
              const parts = dobRaw.split('-');
              if (parts.length === 3) {
                isoDate = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
              }
            }

            if (!cancelled) {
              setLeadPrefill({
                name:     data.name,
                phone:    phoneDigits,
                dob:      dobRaw,
                tob:      data.tob,
                location: data.pob,
                intent:   data.intent,
                gender:   data.gender || '',
              });
            }

            // Bridges — plain storage, written unconditionally so they persist
            // even if the auth check below immediately redirects/unmounts:
            //  • pending-sync → finishAuth syncs birth data to the new account.
            //    The gender key MUST be present here — its absence made the
            //    sync-guest-profile POST 422 (required field), so onboarding_complete
            //    never got set and the user always saw the onboarding form.
            //  • guestBirthData → OnboardingPage pre-fills name/DOB/time/place +
            //    gender. When the funnel already captured gender, OnboardingPage
            //    auto-submits (gated on a truthy gender) and the form is skipped;
            //    when absent it stays blank so the user confirms it with one tap.
            const gender = data.gender || '';
            // Coordinates now flow through prefill (leads.py persists what
            // cosmic-preview geocoded). Carry them so the account is created
            // WITH coords — this is what lets the free session start instead of
            // stalling on the "coordinates missing" wall. null when the lead was
            // never geocoded (falls back to the confirm-screen picker).
            const lat = (typeof data.latitude === 'number') ? data.latitude : null;
            const lon = (typeof data.longitude === 'number') ? data.longitude : null;
            const tz  = data.timezone || null;
            const guestData = { name: data.name, date: isoDate, time: data.tob, place: data.pob, gender, latitude: lat, longitude: lon, timezone: tz };
            try { localStorage.setItem('guest_birth_data_pending_sync', JSON.stringify(guestData)); } catch {}
            try {
              sessionStorage.setItem('guestBirthData', JSON.stringify({
                name:      data.name,
                date:      isoDate,   // YYYY-MM-DD (OnboardingPage handles this form)
                time:      data.tob,
                place:     data.pob,
                gender,
                latitude:  lat,
                longitude: lon,
                timezone:  tz,
              }));
            } catch {}
          }
          // 404 / 429 → skip prefill; user fills the form manually.
        } catch {}
        finally { if (!cancelled) setLeadFetching(false); }
      }

      // 2. Auth check AFTER hydration — redirect returning users, else show form.
      try {
        const res = await fetch(`${API}/api/auth/me`, { credentials: 'include', headers: { ...clientHeaders() } });
        if (res.ok) {
          const user = await res.json();
          const hasBirth = user.date && user.time && user.place;
          // Returning user who already has birth details: clear any prefill blobs
          // the lead-hydration step above may have set, so a stale funnel blob
          // can't later sync over their real data.
          if (hasBirth) {
            try { localStorage.removeItem('guest_birth_data_pending_sync'); } catch {}
            try { sessionStorage.removeItem('guestBirthData'); } catch {}
          }
          navigate(!user.onboarding_complete && !hasBirth ? '/onboarding' : '/dashboard', { replace: true, state: { user } });
          return;
        }
        if ([400, 401, 403].includes(res.status)) {
          localStorage.removeItem('jyotish_session_token');
          localStorage.removeItem('jyotish_auth_user');
        }
      } catch {}
      if (!cancelled) setIsLoading(false);
    })();
    return () => { cancelled = true; };
  }, [navigate]);
  useEffect(() => {
    if (phoneCountdown <= 0) return;
    const t = setTimeout(() => setPhoneCountdown(p => p - 1), 1000);
    return () => clearTimeout(t);
  }, [phoneCountdown]);

  useEffect(() => {
    if (emailCountdown <= 0) return;
    const t = setTimeout(() => setEmailCountdown(p => p - 1), 1000);
    return () => clearTimeout(t);
  }, [emailCountdown]);

  /* ── Post-auth navigation ─────────────────────────────────────────────── */
  const finishAuth = useCallback(async (data) => {
    const token = data.session_token || data.token || data.access_token;
    const user = data.user || {};

    // Sync guest data only for users who genuinely have NO birth details yet.
    // Gating on real emptiness (not just !onboarding_complete) so a returning
    // user who already has date/time/place can never have it overwritten by a
    // stale funnel blob lingering in storage. Returning users get the blob
    // cleared so it can't resurface on a later login.
    const alreadyHasBirth = !!(user.date && user.time && user.place);
    const guestRaw = localStorage.getItem('guest_birth_data_pending_sync');
    if (guestRaw && !alreadyHasBirth && !user.onboarding_complete) {
      try {
        const guest = JSON.parse(guestRaw);
        const syncRes = await fetch(`${API}/api/auth/sync-guest-profile`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, ...clientHeaders() },
          body: JSON.stringify(guest),
        });
        if (syncRes.ok) {
          const syncData = await syncRes.json();
          Object.assign(user, syncData.user);
          localStorage.removeItem('guest_birth_data_pending_sync');
          sessionStorage.removeItem('guestBirthData');
        }
      } catch {}
    } else if (alreadyHasBirth) {
      // Returning user with birth data — drop any stale blob so it can't linger.
      localStorage.removeItem('guest_birth_data_pending_sync');
      sessionStorage.removeItem('guestBirthData');
    }

    await handleAuthCallback(token, user);

    const hasBirth = user.date && user.time && user.place;

    // ── Report-funnel handoff wins over every default destination ───────
    // A buyer who paid on the funnel and is logging in only to READ that
    // report must land on it. Without this they never would: finishAuth
    // ignores authReturnPath entirely (unlike AuthCallback, which honours
    // it on the Google path), so a guest-checkout buyer — necessarily a
    // new phone user with no birth details on their user doc — would be
    // sent to /onboarding, or into /chitragupt when a trial activates
    // below. Either way the report they just paid for is never shown.
    //
    // Onboarding is deliberately skipped here, not suppressed: the report
    // was already generated from the birth details given at checkout, so
    // demanding them again before showing the purchase is pure friction.
    // Nothing marks onboarding complete — the chart features still
    // prompt for it later.


    const handoff = peekReportHandoff();
    if (handoff) {
      const returnPath = sessionStorage.getItem('authReturnPath')
        || `/reports?flow=report&report_id=${encodeURIComponent(handoff.reportId)}`;
      try { sessionStorage.removeItem('authReturnPath'); } catch {}
      navigate(returnPath, { replace: true, state: { user, justAuthenticated: true } });
      return;
    }

    // ── D4: Lead flow & fresh signup lands on Snapshot screen immediately after OTP ──
    const activeLeadId = (leadId || sessionStorage.getItem('lead_id') || new URLSearchParams(window.location.search).get('lead_id') || '').trim();
    const isFreshSignup = !!data?.is_new_user;

    if (activeLeadId || isFreshSignup) {
      try {
        const lang = localStorage.getItem('app_lang') || localStorage.getItem('jyotish_report_lang') || 'hinglish';
        const intent = sessionStorage.getItem('jp_entry_intent') || 'general';
        await fetch(`${API}/api/snapshot`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            lead_id: activeLeadId || undefined,
            intent,
            lang: lang === 'hi' ? 'hi' : (lang === 'en' ? 'en' : 'hinglish'),
          }),
        });
      } catch (err) {
        console.warn('[SNAPSHOT] Post-OTP snapshot call failed:', err);
      }
      navigate('/snapshot', { replace: true, state: { user, justAuthenticated: true, lead_id: activeLeadId || undefined } });
      return;
    }

    // Users without birth data still need onboarding first
    if (!user.onboarding_complete && !hasBirth) {
      navigate('/onboarding', { replace: true, state: { user, justAuthenticated: true } });
      return;
    }

    // ALL authenticated users: check platform trial config
    try {
      const trialConfig = await fetch(`${API}/api/platform/trial-config`).then(r => r.json());
      if (trialConfig.enabled && !user.free_trial_used) {
        const result = await fetch(`${API}/api/subscriptions/trial`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ user_id: user.user_id }),
        }).then(r => r.json());
        if (result.status === 'activated') {
          navigate('/chitragupt?trial_activated=true', { replace: true, state: { user, justAuthenticated: true } });
          return;
        }
      }
    } catch {
      // non-critical — trial check failure must not block login
    }

    navigate('/dashboard', { replace: true, state: { user, justAuthenticated: true } });
  }, [handleAuthCallback, navigate]);

  /* ── Phone OTP ────────────────────────────────────────────────────────── */
  async function sendPhoneOtp() {
    setPhoneError('');
    const p = phone.trim();
    if (!p) { setPhoneError('Enter your mobile number.'); return; }
    if (!/^\d{10}$/.test(p)) { setPhoneError('Enter a valid 10-digit mobile number.'); return; }

    setSendingPhoneOtp(true);
    try {
      const res = await fetch(`${API}/api/auth/request-phone-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: '+91' + p }),
      });
      const data = await res.json();
      if (!res.ok) { setPhoneError(data.detail || 'Unable to send OTP.'); return; }
      setPhoneOtp(['', '', '', '', '', '']);
      setPhoneOtpSent(true);
      setPhoneCountdown(30);
    } catch { setPhoneError('Network error. Please try again.'); }
    finally { setSendingPhoneOtp(false); }
  }

  async function verifyPhoneOtp(otp) {
    if (verifyingPhoneOtp) return;
    setPhoneOtpError('');
    setVerifyingPhoneOtp(true);
    try {
      // Forward the pre-auth guest id (header + body) so the backend folds that
      // doc in place instead of orphaning it behind the new account (JP-118).
      const guestId = localStorage.getItem('guest_user_id') || undefined;
      const guestBody = guestId && guestId.startsWith('guest_') ? { guest_user_id: guestId } : {};
      const res = await fetch(`${API}/api/auth/login-phone-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...clientHeaders(), ...guestHeaders() },
        // Carry lead_id so the backend links lead↔user at account creation.
        body: JSON.stringify({ phone: '+91' + phone.trim(), otp, ...(leadId ? { lead_id: leadId } : {}), fbp: getFbp(), ...guestBody }),
      });
      const data = await res.json();
      if (!res.ok) { setPhoneOtpError(data.detail || 'Invalid OTP.'); return; }
      await finishAuth(data);
    } catch { setPhoneOtpError('Network error.'); }
    finally { setVerifyingPhoneOtp(false); }
  }

  function handlePhoneOtpChange(nextOtp) {
    setPhoneOtp(nextOtp);
    setPhoneOtpError('');
    const joined = nextOtp.join('');
    if (joined.length === 6 && !nextOtp.includes('')) verifyPhoneOtp(joined);
  }

  /* ── Email OTP ────────────────────────────────────────────────────────── */
  async function sendEmailOtp() {
    setEmailError('');
    const e = email.trim();
    if (!e) { setEmailError('Enter your email.'); return; }
    if (!/^\S+@\S+\.\S+$/.test(e)) { setEmailError('Enter a valid email.'); return; }

    setSendingEmailOtp(true);
    try {
      const res = await fetch(`${API}/api/auth/request-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: e.toLowerCase() }),
      });
      const data = await res.json();
      if (!res.ok) { setEmailError(data.detail || 'Unable to send OTP.'); return; }
      setEmailOtp(['', '', '', '', '', '']);
      setEmailOtpSent(true);
      setEmailCountdown(30);
    } catch { setEmailError('Network error.'); }
    finally { setSendingEmailOtp(false); }
  }

  async function verifyEmailOtp(otp) {
    if (verifyingEmailOtp) return;
    setEmailOtpError('');
    setVerifyingEmailOtp(true);
    try {
      const guestId = localStorage.getItem('guest_user_id') || undefined;
      const guestBody = guestId && guestId.startsWith('guest_') ? { guest_user_id: guestId } : {};
      const res = await fetch(`${API}/api/auth/login-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...clientHeaders(), ...guestHeaders() },
        body: JSON.stringify({ email: email.trim().toLowerCase(), otp, fbp: getFbp(), ...guestBody }),
      });
      const data = await res.json();
      if (!res.ok) { setEmailOtpError(data.detail || 'Invalid OTP.'); return; }
      await finishAuth(data);
    } catch { setEmailOtpError('Network error.'); }
    finally { setVerifyingEmailOtp(false); }
  }

  function handleEmailOtpChange(nextOtp) {
    setEmailOtp(nextOtp);
    setEmailOtpError('');
    const joined = nextOtp.join('');
    if (joined.length === 6 && !nextOtp.includes('')) verifyEmailOtp(joined);
  }

  /* ── Password auth ────────────────────────────────────────────────────── */
  async function handlePasswordSubmit(e) {
    e?.preventDefault();
    setPwdEmailError(''); setPasswordError(''); setConfirmError(''); setNameError('');
    let valid = true;
    if (authMode === 'signup' && !name.trim()) { setNameError('Enter your name.'); valid = false; }
    if (!pwdEmail.trim() || !/^\S+@\S+\.\S+$/.test(pwdEmail.trim())) { setPwdEmailError('Enter a valid email.'); valid = false; }
    if (!password.trim()) { setPasswordError('Enter your password.'); valid = false; }
    else if (authMode === 'signup' && password.length < 6) { setPasswordError('Min 6 characters.'); valid = false; }
    if (authMode === 'signup') {
      if (!confirmPassword.trim()) { setConfirmError('Confirm your password.'); valid = false; }
      else if (confirmPassword !== password) { setConfirmError('Passwords do not match.'); valid = false; }
    }
    if (!valid) return;

    setPwdSubmitting(true);
    try {
      const endpoint = authMode === 'login' ? '/api/auth/login-password' : '/api/auth/signup';
      const payload = authMode === 'login'
        ? { email: pwdEmail.trim().toLowerCase(), password }
        : { name: name.trim(), email: pwdEmail.trim().toLowerCase(), password, confirm_password: confirmPassword, fbp: getFbp() };
      const res = await fetch(`${API}${endpoint}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...clientHeaders() }, body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        const d = data.detail || data.message || '';
        if (/already|exists|duplicate/i.test(d)) setPwdEmailError('Account exists. Log in instead.');
        else if (/password/i.test(d)) setPasswordError(d);
        else if (/email/i.test(d)) setPwdEmailError(d);
        else setPasswordError(d || 'Failed. Try again.');
        return;
      }
      await finishAuth(data);
    } catch { setPasswordError('Network error.'); }
    finally { setPwdSubmitting(false); }
  }

  async function handleForgot() {
    setPwdEmailError('');
    if (!pwdEmail.trim() || !/^\S+@\S+\.\S+$/.test(pwdEmail.trim())) { setPwdEmailError('Enter your email first.'); return; }
    setForgotSubmitting(true);
    try {
      await fetch(`${API}/api/auth/forgot-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: pwdEmail.trim().toLowerCase() }),
      });
      setError(null);
      setPwdEmailError('If your account exists, reset instructions have been sent.');
    } catch {}
    finally { setForgotSubmitting(false); }
  }

  /* ── Google One Tap ───────────────────────────────────────────────────── */
  const handleGoogleCredential = useCallback(async (response) => {
    setIsAuthenticating(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/auth/google-one-tap`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...clientHeaders() }, credentials: 'include',
        body: JSON.stringify({ credential: response.credential, fbp: getFbp() }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Auth failed'); }
      const data = await res.json();
      await finishAuth(data);
    } catch (err) {
      setError(err.message);
      setIsAuthenticating(false);
    }
  }, [finishAuth]);

  useEffect(() => {
    if (isLoading || oneTapInitialized.current) return;
    let cancelled = false;
    const GSI_SRC = 'https://accounts.google.com/gsi/client';
    const init = () => {
      if (cancelled || oneTapInitialized.current) return;
      if (!window.google?.accounts?.id) { setTimeout(init, 100); return; }
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID, callback: handleGoogleCredential,
        auto_select: false, cancel_on_tap_outside: true, context: 'signin', ux_mode: 'popup', itp_support: true,
      });
      if (googleButtonRef.current) {
        window.google.accounts.id.renderButton(googleButtonRef.current, {
          type: 'standard', theme: 'filled_black', size: 'large', text: 'continue_with', shape: 'pill', logo_alignment: 'left', width: 300,
        });
      }
      window.google.accounts.id.prompt();
      oneTapInitialized.current = true;
    };
    const loadGsi = () => {
      if (cancelled || oneTapInitialized.current) return;
      if (typeof document !== 'undefined' && !window.google?.accounts?.id && !document.querySelector(`script[src="${GSI_SRC}"]`)) {
        const s = document.createElement('script');
        s.src = GSI_SRC;
        s.async = true;
        s.defer = true;
        document.head.appendChild(s);
      }
      init();
    };
    const gsiEvents = ['pointerdown', 'touchstart', 'keydown', 'focusin', 'mousemove'];
    const onUserInteract = () => {
      gsiEvents.forEach((evt) => window.removeEventListener(evt, onUserInteract));
      loadGsi();
    };
    gsiEvents.forEach((evt) => window.addEventListener(evt, onUserInteract, { once: true, passive: true }));
    return () => {
      cancelled = true;
      gsiEvents.forEach((evt) => window.removeEventListener(evt, onUserInteract));
      window.google?.accounts?.id?.cancel();
    };
  }, [isLoading, handleGoogleCredential]);

  /* ── Loading state ────────────────────────────────────────────────────── */
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-[#13082a] to-black flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-amber-400 animate-spin" />
      </div>
    );
  }

  /* ── Render ───────────────────────────────────────────────────────────── */
  return (
    <>
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-[#13082a] to-black flex items-start justify-center px-4 pt-8 pb-16 relative overflow-x-hidden overflow-y-auto">
      {/* Stars */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[['top-20 left-10','w-2 h-2 bg-amber-400/60',''],['top-32 right-24','w-1 h-1 bg-white/50','delay-75'],['top-40 right-20','w-1.5 h-1.5 bg-amber-300/40','delay-100'],['top-60 left-1/4','w-1 h-1 bg-amber-200/50','delay-200'],['top-1/3 right-1/4','w-1.5 h-1.5 bg-white/30','delay-[250ms]'],['bottom-40 right-1/3','w-2 h-2 bg-amber-400/30','delay-300'],['bottom-60 left-20','w-1.5 h-1.5 bg-amber-300/40','delay-150'],['bottom-32 right-16','w-1 h-1 bg-white/40','delay-200']].map(([pos,sz,del],i)=>(
          <div key={i} className={`absolute ${pos} ${sz} rounded-full animate-pulse ${del}`}/>
        ))}
      </div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-amber-500/10 rounded-full blur-3xl z-0" />

      <div className="max-w-md w-full relative z-10">
        {/* Branding */}
        <div className="text-center mb-8">
          <div className="inline-block mb-5">
            <img src="/logo-mobile.png" alt="JyotishPuraan" className="w-20 h-20 rounded-2xl drop-shadow-[0_0_15px_rgba(251,191,36,0.5)]" />
          </div>
          <h1 className="text-3xl font-serif font-bold text-amber-100 mb-2 tracking-wide">JyotishPuraan</h1>
          <p className="text-amber-200/50 text-sm italic">Where ancient stars guide your modern journey</p>
        </div>

        {/* Tab Switcher */}
        <div className="flex mb-5 bg-white/5 rounded-2xl p-1 border border-white/5">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => { setActiveTab(t.key); setError(null); }}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold transition-all ${active ? 'bg-amber-500/15 text-amber-400 border border-amber-500/25' : 'text-white/40 hover:text-white/60'}`}
                data-testid={`tab-${t.key}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Login Card */}
        <div className="bg-white/[0.04] backdrop-blur-xl rounded-2xl border border-amber-500/15 shadow-2xl shadow-amber-900/20 p-6">

          {error && (
            <div className="bg-red-500/15 border border-red-500/40 rounded-xl p-3 mb-5">
              <p className="text-red-300 text-sm text-center">{error}</p>
            </div>
          )}

          {/* ── Lead Prefill Banner ───────────────────────────────────── */}
          {leadFetching && (
            <div className="mb-5 rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 animate-pulse space-y-2">
              <div className="h-3 bg-white/10 rounded w-2/3" />
              <div className="h-3 bg-white/10 rounded w-1/2" />
              <div className="h-3 bg-white/10 rounded w-3/4" />
            </div>
          )}

          {!leadFetching && leadPrefill && (
            <div className="mb-5 rounded-xl border border-amber-500/25 bg-amber-500/8 p-4" data-testid="lead-prefill-banner">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-4 h-4 text-amber-400 shrink-0" />
                <p className="text-amber-300 text-sm font-semibold">Chart details loaded from your preview</p>
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
                {leadPrefill.name && (
                  <div className="flex items-center gap-1.5 text-white/70">
                    <User className="w-3 h-3 text-amber-500/60 shrink-0" />
                    <span className="truncate">{leadPrefill.name}</span>
                  </div>
                )}
                {leadPrefill.dob && (
                  <div className="flex items-center gap-1.5 text-white/70">
                    <Calendar className="w-3 h-3 text-amber-500/60 shrink-0" />
                    <span>{leadPrefill.dob}{leadPrefill.tob ? ` · ${leadPrefill.tob}` : ''}</span>
                  </div>
                )}
                {leadPrefill.location && (
                  <div className="flex items-center gap-1.5 text-white/70">
                    <MapPin className="w-3 h-3 text-amber-500/60 shrink-0" />
                    <span className="truncate">{leadPrefill.location}</span>
                  </div>
                )}
                {leadPrefill.gender && (
                  <div className="flex items-center gap-1.5 text-white/70">
                    <span className="text-amber-500/60">⊙</span>
                    <span>{leadPrefill.gender}</span>
                  </div>
                )}
              </div>
              <p className="text-white/35 text-xs mt-3">These details will be saved to your account after you sign in.</p>
            </div>
          )}

          {isAuthenticating && (
            <div className="flex flex-col items-center py-6">
              <Loader2 className="w-8 h-8 text-amber-400 animate-spin mb-3" />
              <p className="text-amber-200/60 text-sm">Authenticating...</p>
            </div>
          )}

          {!isAuthenticating && (
            <>
              {/* ── Mobile OTP Tab ───────────────────────────────────── */}
              {activeTab === 'mobile' && (
                <div className="space-y-4">
                  <div>
                    <h2 className="text-xl font-semibold text-white mb-1">Continue with Mobile</h2>
                    <p className="text-white/40 text-xs">We'll send a one-time password to verify your number.</p>
                  </div>
                  <div className="flex items-center gap-0 bg-white/5 rounded-xl border border-amber-500/20 overflow-hidden focus-within:border-amber-400/50 transition">
                    <span className="text-white font-semibold text-sm px-3.5 border-r border-white/10">+91</span>
                    <input
                      type="tel" maxLength={10} value={phone}
                      onChange={e => { setPhone(e.target.value.replace(/\D/g, '').slice(0, 10)); setPhoneError(''); }}
                      placeholder="Enter mobile number"
                      className="flex-1 bg-transparent text-white text-sm px-3 py-3 outline-none placeholder:text-white/30"
                      data-testid="phone-input"
                    />
                  </div>
                  {phoneError && <p className="text-red-400 text-xs">{phoneError}</p>}

                  <button onClick={sendPhoneOtp} disabled={sendingPhoneOtp || verifyingPhoneOtp}
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-[#120A00] font-bold text-sm hover:from-amber-400 hover:to-amber-500 transition disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="send-phone-otp-btn"
                  >
                    {sendingPhoneOtp ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send OTP'}
                  </button>

                  {phoneOtpSent && (
                    <div className="space-y-3 pt-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
                      <p className="text-white/70 text-xs font-medium">Enter 6-digit OTP</p>
                      <OtpInput value={phoneOtp} onChange={handlePhoneOtpChange} disabled={verifyingPhoneOtp} />
                      {phoneOtpError && <p className="text-red-400 text-xs">{phoneOtpError}</p>}
                      {verifyingPhoneOtp && <div className="flex justify-center"><Loader2 className="w-5 h-5 text-amber-400 animate-spin" /></div>}
                      <div className="text-center">
                        {phoneCountdown > 0
                          ? <span className="text-white/40 text-xs">Resend in {phoneCountdown}s</span>
                          : <button onClick={sendPhoneOtp} className="text-amber-400 text-xs font-semibold hover:text-amber-300 transition">Resend OTP</button>
                        }
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Email OTP Tab ────────────────────────────────────── */}
              {activeTab === 'email' && (
                <div className="space-y-4">
                  <div>
                    <h2 className="text-xl font-semibold text-white mb-1">Continue with Email OTP</h2>
                    <p className="text-white/40 text-xs">A verification code will be sent to your inbox.</p>
                  </div>
                  <input
                    type="email" value={email}
                    onChange={e => { setEmail(e.target.value); setEmailError(''); }}
                    placeholder="Email address"
                    className="w-full bg-white/5 border border-amber-500/20 rounded-xl px-4 py-3 text-white text-sm outline-none placeholder:text-white/30 focus:border-amber-400/50 transition"
                    data-testid="email-otp-input"
                  />
                  {emailError && <p className="text-red-400 text-xs">{emailError}</p>}

                  <button onClick={sendEmailOtp} disabled={sendingEmailOtp || verifyingEmailOtp}
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-[#120A00] font-bold text-sm hover:from-amber-400 hover:to-amber-500 transition disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="send-email-otp-btn"
                  >
                    {sendingEmailOtp ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send OTP'}
                  </button>

                  {emailOtpSent && (
                    <div className="space-y-3 pt-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
                      <p className="text-white/70 text-xs font-medium">Enter 6-digit OTP</p>
                      <OtpInput value={emailOtp} onChange={handleEmailOtpChange} disabled={verifyingEmailOtp} />
                      {emailOtpError && <p className="text-red-400 text-xs">{emailOtpError}</p>}
                      {verifyingEmailOtp && <div className="flex justify-center"><Loader2 className="w-5 h-5 text-amber-400 animate-spin" /></div>}
                      <div className="text-center">
                        {emailCountdown > 0
                          ? <span className="text-white/40 text-xs">Resend in {emailCountdown}s</span>
                          : <button onClick={sendEmailOtp} className="text-amber-400 text-xs font-semibold hover:text-amber-300 transition">Resend OTP</button>
                        }
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Password Tab ─────────────────────────────────────── */}
              {activeTab === 'password' && (
                <form onSubmit={handlePasswordSubmit} className="space-y-4">
                  {/* Login / Sign Up toggle */}
                  <div className="flex bg-white/5 rounded-xl p-1 mb-1">
                    {['login', 'signup'].map(m => (
                      <button key={m} type="button" onClick={() => setAuthMode(m)}
                        className={`flex-1 py-2 rounded-lg text-xs font-semibold transition ${authMode === m ? 'bg-amber-500/15 text-amber-400' : 'text-white/40 hover:text-white/60'}`}
                      >
                        {m === 'login' ? 'Login' : 'Sign Up'}
                      </button>
                    ))}
                  </div>

                  {authMode === 'signup' && (
                    <>
                      <input type="text" value={name} onChange={e => { setName(e.target.value); setNameError(''); }}
                        placeholder="Full name"
                        className="w-full bg-white/5 border border-amber-500/20 rounded-xl px-4 py-3 text-white text-sm outline-none placeholder:text-white/30 focus:border-amber-400/50 transition"
                        data-testid="name-input"
                      />
                      {nameError && <p className="text-red-400 text-xs">{nameError}</p>}
                    </>
                  )}

                  <input type="email" value={pwdEmail} onChange={e => { setPwdEmail(e.target.value); setPwdEmailError(''); }}
                    placeholder="Email address"
                    className="w-full bg-white/5 border border-amber-500/20 rounded-xl px-4 py-3 text-white text-sm outline-none placeholder:text-white/30 focus:border-amber-400/50 transition"
                    data-testid="pwd-email-input"
                  />
                  {pwdEmailError && <p className="text-red-400 text-xs">{pwdEmailError}</p>}

                  <div className="flex items-center bg-white/5 border border-amber-500/20 rounded-xl px-4 focus-within:border-amber-400/50 transition">
                    <input type={showPassword ? 'text' : 'password'} value={password}
                      onChange={e => { setPassword(e.target.value); setPasswordError(''); }}
                      placeholder="Password"
                      className="flex-1 bg-transparent py-3 text-white text-sm outline-none placeholder:text-white/30"
                      data-testid="password-input"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-white/40 hover:text-white/60 transition">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {passwordError && <p className="text-red-400 text-xs">{passwordError}</p>}

                  {authMode === 'signup' && (
                    <>
                      <div className="flex items-center bg-white/5 border border-amber-500/20 rounded-xl px-4 focus-within:border-amber-400/50 transition">
                        <input type={showConfirm ? 'text' : 'password'} value={confirmPassword}
                          onChange={e => { setConfirmPassword(e.target.value); setConfirmError(''); }}
                          placeholder="Confirm password"
                          className="flex-1 bg-transparent py-3 text-white text-sm outline-none placeholder:text-white/30"
                          data-testid="confirm-password-input"
                        />
                        <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="text-white/40 hover:text-white/60 transition">
                          {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {confirmError && <p className="text-red-400 text-xs">{confirmError}</p>}
                    </>
                  )}

                  {authMode === 'login' && (
                    <div className="text-right">
                      <button type="button" onClick={handleForgot} disabled={forgotSubmitting}
                        className="text-amber-400 text-xs font-semibold hover:text-amber-300 transition disabled:opacity-50"
                        data-testid="forgot-password-btn"
                      >
                        {forgotSubmitting ? 'Sending...' : 'Forgot Password?'}
                      </button>
                    </div>
                  )}

                  <button type="submit" disabled={pwdSubmitting}
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-[#120A00] font-bold text-sm hover:from-amber-400 hover:to-amber-500 transition disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="password-submit-btn"
                  >
                    {pwdSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : authMode === 'login' ? 'Login' : 'Create Account'}
                  </button>
                </form>
              )}

              {/* ── Divider ──────────────────────────────────────────── */}
              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-white/30 text-[10px] tracking-widest uppercase">or</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>

              {/* ── Google Button ─────────────────────────────────────── */}
              <div className="flex justify-center">
                <div ref={googleButtonRef} id="google-signin-button" className="google-btn-container" />
              </div>
            </>
          )}

          <p className="text-white/30 text-[10px] text-center mt-5">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>

        {/* Features Preview */}
        <div className="mt-8 grid grid-cols-3 gap-3">
          {[
            { Icon: Brain, label: 'AI Predictions' },
            { Icon: ScrollText, label: 'Birth Charts' },
            { Icon: Clock, label: 'Dasha Analysis' },
          ].map(({ Icon, label }) => (
            <div key={label} className="bg-white/5 border border-white/10 rounded-xl p-4 backdrop-blur-md text-center group hover:border-amber-500/30 transition-colors">
              <div className="flex justify-center mb-3">
                <Icon className="w-7 h-7 text-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.4)] group-hover:drop-shadow-[0_0_12px_rgba(251,191,36,0.6)] transition-all" />
              </div>
              <p className="text-[10px] tracking-[0.2em] text-amber-200/70 uppercase font-medium">{label}</p>
            </div>
          ))}
        </div>

        <div className="mt-5 text-center">
          <a href="/onboarding" className="text-slate-500 hover:text-slate-400 text-sm transition-colors" data-testid="back-to-onboarding-link">
            &larr; Back to Onboarding
          </a>
        </div>
      </div>
    </div>
    <LegalFooter />
    </>
  );
};

export default LoginPage;
